import React from "react";
import { ShieldCheck } from "lucide-react";
import { GENDER_LABELS, PROGRAM_LABELS } from "@/lib/api";
import { Input } from "@/components/ui/input";

const GENDERS = ["man", "woman", "nonbinary"];

/**
 * Recovery pathway, gender + gender preference, location and language.
 *
 * Extracted verbatim from src/pages/Settings.jsx during the code-quality pass:
 * markup, copy, data-testids and behaviour are unchanged.
 */
export default function RecoveryDetailsCard({ form, setForm, programs }) {
  return (
    <div className="sm-card p-6 bg-white">
      <label className="text-xs font-bold uppercase tracking-wide text-[#4B5A60]">
        My recovery pathway
      </label>
      <div className="mt-2 flex flex-wrap gap-2">
        {programs.map((p) => (
          <button
            key={p}
            type="button"
            onClick={() => setForm({ ...form, recovery_program: p })}
            data-testid={`settings-program-${p}`}
            className={`px-3 py-2 rounded-xl text-sm font-semibold border-2 bg-white ${
              form.recovery_program === p
                ? "border-[#1F6F78] text-[#1F6F78] bg-[#F2FAFA]"
                : "border-[#E3DED4] text-[#4B5A60]"
            }`}
          >
            {PROGRAM_LABELS[p]}
          </button>
        ))}
      </div>

      <div className="mt-5 grid gap-4 sm:grid-cols-2">
        <div>
          <label className="text-xs font-bold uppercase tracking-wide text-[#4B5A60]">
            My gender
          </label>
          <select
            value={form.gender}
            onChange={(e) => setForm({ ...form, gender: e.target.value })}
            className="mt-1.5 w-full h-11 rounded-xl border border-[#E3DED4] bg-white px-3 text-sm"
          >
            <option value="">Prefer not to say</option>
            {GENDERS.map((g) => (
              <option key={g} value={g}>
                {GENDER_LABELS[g]}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-xs font-bold uppercase tracking-wide text-[#4B5A60]">
            Sponsor gender preference
          </label>
          <select
            value={form.gender_preference}
            onChange={(e) => setForm({ ...form, gender_preference: e.target.value })}
            data-testid="settings-gender-preference"
            className="mt-1.5 w-full h-11 rounded-xl border border-[#E3DED4] bg-white px-3 text-sm"
          >
            <option value="no_preference">No preference</option>
            {GENDERS.map((g) => (
              <option key={g} value={g}>
                {GENDER_LABELS[g]}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="mt-4 grid gap-4 sm:grid-cols-4">
        <div>
          <label className="text-xs font-bold uppercase tracking-wide text-[#4B5A60]">City</label>
          <Input
            value={form.city}
            onChange={(e) => setForm({ ...form, city: e.target.value })}
            className="mt-1.5 h-11 bg-white border-[#E3DED4]"
          />
        </div>
        <div>
          <label className="text-xs font-bold uppercase tracking-wide text-[#4B5A60]">State</label>
          <Input
            value={form.state}
            onChange={(e) => setForm({ ...form, state: e.target.value })}
            className="mt-1.5 h-11 bg-white border-[#E3DED4]"
          />
        </div>
        <div>
          <label className="text-xs font-bold uppercase tracking-wide text-[#4B5A60]">
            Country
          </label>
          <Input
            value={form.country}
            onChange={(e) => setForm({ ...form, country: e.target.value })}
            className="mt-1.5 h-11 bg-white border-[#E3DED4]"
          />
        </div>
        <div>
          <label className="text-xs font-bold uppercase tracking-wide text-[#4B5A60]">
            Language
          </label>
          <Input
            value={form.language}
            onChange={(e) => setForm({ ...form, language: e.target.value })}
            className="mt-1.5 h-11 bg-white border-[#E3DED4]"
          />
        </div>
      </div>
      <p className="mt-3 flex items-start gap-2 text-xs text-[#7A868B] leading-5">
        <ShieldCheck className="w-4 h-4 shrink-0 text-[#1F6F78]" />
        We never ask for your full legal name, exact address, employer, sobriety date or medical
        history, and we never display your exact location.
      </p>
    </div>
  );
}
